/**
 * React Query hook for L3 Taška★ offer@SP bag list.
 * Kept outside offerFavoritesApi so session bootstrap can import claim helpers
 * without a circular graph into CustomerSessionProvider.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { ACCOUNT_ME_STALE_MS } from '../../shared/session/fetchPlatformMe.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import { fetchOfferFavorites } from './offerFavoritesApi.js';
import type { ListCustomerOfferFavoritesResultDTO } from './offerFavoritesTypes.js';

const OFFER_FAVORITES_QUERY_OPTIONS = {
  staleTime: ACCOUNT_ME_STALE_MS,
  refetchOnMount: false as const,
  refetchOnWindowFocus: false,
};

/**
 * GET offer bag via L3 absolute `/api/v1/...` + credentials include.
 * Enablement matches global chrome — `/me`-established session only.
 */
export function useOfferFavoritesByToken(
  enabled = true,
): UseQueryResult<ListCustomerOfferFavoritesResultDTO, Error> {
  const { apiBaseUrl } = useRuntimeConfig();
  const { session } = useCustomerSession();
  const signedInChrome = isGloballySignedInChrome({ session });
  const customerKey =
    session?.customerId ?? (signedInChrome ? 'session' : -1);

  return useQuery({
    queryKey: customerQueryKeys.offerFavorites(customerKey),
    queryFn: ({ signal }) =>
      fetchOfferFavorites({
        apiBaseUrl,
        signal,
      }),
    enabled: enabled && signedInChrome,
    ...OFFER_FAVORITES_QUERY_OPTIONS,
  });
}
